import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import { THEME_PRESETS } from '@/utils/themeManager'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api'

export const useTenantBrandingStore = defineStore('tenantBranding', () => {
    // State
    const branding = ref(null)
    const isLoading = ref(false)
    const error = ref(null)

    // Computed
    const isCustomBranded = computed(() => {
        return branding.value !== null && branding.value.customized === true
    })

    const primaryColor = computed(() => {
        return branding.value?.colors?.primary || '#8B4513'
    })

    const secondaryColor = computed(() => {
        return branding.value?.colors?.secondary || '#FFD700'
    })

    const logo = computed(() => {
        return branding.value?.logo?.url || null
    })

    const businessName = computed(() => {
        return branding.value?.businessName || 'Pasticceria'
    })

    const favicon = computed(() => {
        return branding.value?.favicon?.url || null
    })

    const customDomain = computed(() => {
        return branding.value?.customDomain || null
    })

    const themePreset = computed(() => {
        return branding.value?.preset || 'classic'
    })

    const fonts = computed(() => {
        return branding.value?.fonts || {
            display: 'Playfair Display',
            body: 'Inter'
        }
    })

    // Actions

    /**
     * Fetch branding configuration from API
     * @param {string} tenantId - Tenant ID
     */
    const fetchBranding = async (tenantId) => {
        isLoading.value = true
        error.value = null

        try {
            const response = await axios.get(`${API_URL}/tenants/${tenantId}/branding`)
            branding.value = response.data

            // If no branding exists, set default
            if (!branding.value) {
                branding.value = getDefaultBranding()
            }

            return branding.value
        } catch (err) {
            console.error('Failed to fetch branding:', err)
            error.value = err.response?.data?.message || 'Failed to load branding'
            // Use default branding on error
            branding.value = getDefaultBranding()
            throw err
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Update branding configuration
     * @param {Object} config - Branding configuration
     */
    const updateBranding = async (config) => {
        isLoading.value = true
        error.value = null

        try {
            const response = await axios.put(
                `${API_URL}/tenants/branding`,
                config
            )
            branding.value = response.data
            return response.data
        } catch (err) {
            console.error('Failed to update branding:', err)
            error.value = err.response?.data?.message || 'Failed to update branding'
            throw err
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Upload logo file
     * @param {File} file - Logo image file
     */
    const uploadLogo = async (file) => {
        isLoading.value = true
        error.value = null

        try {
            const formData = new FormData()
            formData.append('logo', file)

            const response = await axios.post(
                `${API_URL}/tenants/branding/logo`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            )

            // Update branding with new logo
            if (branding.value) {
                branding.value.logo = response.data.logo
            }

            return response.data
        } catch (err) {
            console.error('Failed to upload logo:', err)
            error.value = err.response?.data?.message || 'Failed to upload logo'
            throw err
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Upload favicon file
     * @param {File} file - Favicon image file
     */
    const uploadFavicon = async (file) => {
        isLoading.value = true
        error.value = null

        try {
            const formData = new FormData()
            formData.append('favicon', file)

            const response = await axios.post(
                `${API_URL}/tenants/branding/favicon`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            )

            // Update branding with new favicon
            if (branding.value) {
                branding.value.favicon = response.data.favicon
            }

            return response.data
        } catch (err) {
            console.error('Failed to upload favicon:', err)
            error.value = err.response?.data?.message || 'Failed to upload favicon'
            throw err
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Reset branding to default
     */
    const resetBranding = async () => {
        isLoading.value = true
        error.value = null

        try {
            const response = await axios.delete(`${API_URL}/tenants/branding`)
            branding.value = response.data || getDefaultBranding()
            return branding.value
        } catch (err) {
            console.error('Failed to reset branding:', err)
            error.value = err.response?.data?.message || 'Failed to reset branding'
            throw err
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Apply theme preset
     * @param {string} preset - Preset name (classic, modern, elegant, fresh)
     */
    const applyPreset = async (preset) => {
        if (!THEME_PRESETS[preset]) {
            throw new Error(`Invalid preset: ${preset}`)
        }

        const presetConfig = THEME_PRESETS[preset]

        return await updateBranding({
            preset,
            colors: presetConfig.colors,
            fonts: presetConfig.fonts,
            customized: true
        })
    }

    /**
     * Validate custom domain
     * @param {string} domain - Domain to validate
     */
    const validateDomain = async (domain) => {
        try {
            const response = await axios.post(`${API_URL}/tenants/branding/validate-domain`, {
                domain
            })
            return response.data
        } catch (err) {
            console.error('Domain validation failed:', err)
            throw err
        }
    }

    /**
     * Get tenant by custom domain
     * @param {string} domain - Custom domain
     */
    const getTenantByDomain = async (domain) => {
        try {
            const response = await axios.get(`${API_URL}/tenants/by-domain/${domain}`)
            return response.data
        } catch (err) {
            console.error('Failed to get tenant by domain:', err)
            throw err
        }
    }

    /**
     * Get default branding configuration
     */
    const getDefaultBranding = () => {
        return {
            customized: false,
            businessName: 'Pasticceria',
            colors: THEME_PRESETS.classic.colors,
            fonts: THEME_PRESETS.classic.fonts,
            preset: 'classic',
            logo: null,
            favicon: null,
            customDomain: null,
            darkMode: {
                enabled: false
            }
        }
    }

    /**
     * Update business name
     * @param {string} name - New business name
     */
    const updateBusinessName = async (name) => {
        return await updateBranding({
            ...branding.value,
            businessName: name,
            customized: true
        })
    }

    /**
     * Update colors
     * @param {Object} colors - Color configuration
     */
    const updateColors = async (colors) => {
        return await updateBranding({
            ...branding.value,
            colors: {
                ...branding.value?.colors,
                ...colors
            },
            customized: true
        })
    }

    /**
     * Update fonts
     * @param {Object} fonts - Font configuration
     */
    const updateFonts = async (fonts) => {
        return await updateBranding({
            ...branding.value,
            fonts: {
                ...branding.value?.fonts,
                ...fonts
            },
            customized: true
        })
    }

    /**
     * Update custom domain
     * @param {string} domain - Custom domain
     */
    const updateCustomDomain = async (domain) => {
        // Validate domain first
        const validation = await validateDomain(domain)

        if (!validation.valid) {
            throw new Error(validation.error || 'Invalid domain')
        }

        return await updateBranding({
            ...branding.value,
            customDomain: domain,
            customized: true
        })
    }

    /**
     * Toggle dark mode
     * @param {boolean} enabled - Enable dark mode
     */
    const toggleDarkMode = async (enabled) => {
        return await updateBranding({
            ...branding.value,
            darkMode: {
                ...branding.value?.darkMode,
                enabled
            },
            customized: true
        })
    }

    /**
     * Update custom CSS
     * @param {string} css - Custom CSS
     */
    const updateCustomCSS = async (css) => {
        return await updateBranding({
            ...branding.value,
            customCSS: css,
            customized: true
        })
    }

    /**
     * Clear error
     */
    const clearError = () => {
        error.value = null
    }

    return {
        // State
        branding,
        isLoading,
        error,

        // Computed
        isCustomBranded,
        primaryColor,
        secondaryColor,
        logo,
        businessName,
        favicon,
        customDomain,
        themePreset,
        fonts,

        // Actions
        fetchBranding,
        updateBranding,
        uploadLogo,
        uploadFavicon,
        resetBranding,
        applyPreset,
        validateDomain,
        getTenantByDomain,
        getDefaultBranding,
        updateBusinessName,
        updateColors,
        updateFonts,
        updateCustomDomain,
        toggleDarkMode,
        updateCustomCSS,
        clearError
    }
}, {
    persist: {
        paths: ['branding']
    }
})
